import React, { useEffect, useMemo, useState } from "react";
import { FeatureCollection } from "geojson";
import { Layer, Source } from "react-map-gl/maplibre";
import { Outlet, useParams, useRouteLoaderData } from "react-router";
import { FilterSpecification } from "@maplibre/maplibre-gl-style-spec";
import MapControl from "../../MapControl";
import Popup from "../../Popup";
import { useTranslation } from "react-i18next";
import AppleCountLegend from "./AppleCountLegend";
import {
  YieldEstimate,
  getCalibrationMeasurementSet,
  getYieldEstimate,
} from "../../../../API/calibration";
import { ResultSet } from "../../../../API/fields";
import { CalibratedTreeCount } from "./calibration/CalibratedTreeCount";
import { YieldCard } from "../../../../routes/customer/_customer_id/field/_field_id/result-set/calibrate/map/YieldCard";
import {
  calculateAppleThresholds,
  getAverageAppleCountPerTree,
} from "./apple-count-helper";
import { ErrorAlert } from "../../../Error/ErrorAlert";
import {
  NUMBER_OF_COLORS,
  bucketRange,
  colorVarForBucket,
} from "./apple-count-colors";
import { buildBucketPaint } from "./bucket-paint";
import { CutoffRange } from "../../../../routes/customer/_customer_id/taskmap/taskmap.types";
import {
  AppleCountFeatureCollection,
  AppleCountFeatureProperties,
} from "../layer.types";

const NO_CUTOFF_MAX = Number.MAX_VALUE;

const APPLE_COUNT_PROPERTY = {
  CALIBRATED: "calibrated_count",
  UNCALIBRATED: "apple_count",
} as const;

type AppleCountProperty =
  (typeof APPLE_COUNT_PROPERTY)[keyof typeof APPLE_COUNT_PROPERTY];

type AppleLayerProps = {
  geojson: AppleCountFeatureCollection;
  cutoff?: CutoffRange;
};

export function AppleCountLayer({
  geojson,
  cutoff,
}: AppleLayerProps): React.ReactNode {
  const { t } = useTranslation();
  const { resultSetId } = useParams();
  const resultSets = useRouteLoaderData("field") as ResultSet[];

  const [calibrationDate, setCalibrationDate] = useState<string | null>(null);
  const [yieldEstimate, setYieldEstimate] = useState<YieldEstimate>();
  const [manualCountsCount, setManualCountsCount] = useState<number>(0);
  const [error, setError] = useState("");

  const isCalibrated = useMemo(
    () => geojson.features.some((f) => f.properties.calibrated_count != null),
    [geojson],
  );

  const averageAppleCountPerTree = getAverageAppleCountPerTree(geojson);

  const activeProperty: AppleCountProperty = isCalibrated
    ? APPLE_COUNT_PROPERTY.CALIBRATED
    : APPLE_COUNT_PROPERTY.UNCALIBRATED;

  const buckets = useMemo(
    () =>
      calculateAppleThresholds(geojson.features, {
        property: activeProperty,
        bucketCount: NUMBER_OF_COLORS,
      }),
    [geojson, activeProperty],
  );

  const { min: legendMin, max: legendMax } = useMemo(
    () => bucketRange(buckets),
    [buckets],
  );

  const paint = useMemo(
    () =>
      buildBucketPaint(buckets, {
        property: activeProperty,
        colorVar: colorVarForBucket,
        mode: "interpolate",
        opacity: 0.7,
      }),
    [buckets, activeProperty],
  );

  useEffect(() => {
    const measurementSetId = resultSets.find((rs) => rs.id === resultSetId)
      ?.calibration?.calibration_measurement_set_id;

    let isMounted = true;

    async function loadData() {
      if (!measurementSetId || !resultSetId) {
        setCalibrationDate(null);
        return;
      }

      try {
        const [calibrationData, yieldEstimateData] = await Promise.all([
          getCalibrationMeasurementSet(measurementSetId),
          getYieldEstimate(resultSetId),
        ]);

        if (isMounted) {
          setCalibrationDate(calibrationData.created_at);
          setYieldEstimate(yieldEstimateData);
          setManualCountsCount(calibrationData.measurements.length);
        }
      } catch (error) {
        console.error(
          "Failed to load calibration data and yield estimation data",
          error,
        );
        setError(`${t("text.error_occured")}. ${t("text.refresh_page")}`);
      }
    }

    loadData();

    return () => {
      isMounted = false;
    };
  }, [resultSetId]);

  const layerFilter = useMemo(
    () =>
      [
        "all",
        ["!=", ["get", activeProperty], 0],
        [">=", ["get", activeProperty], cutoff ? cutoff.min : 0],
        ["<=", ["get", activeProperty], cutoff ? cutoff.max : NO_CUTOFF_MAX],
      ] as FilterSpecification,
    [cutoff, activeProperty],
  );

  return (
    <>
      <Source id="apple" type="geojson" data={geojson as FeatureCollection}>
        <Outlet />
        <Layer
          beforeId="result"
          type="fill"
          id="apple-fill"
          filter={layerFilter}
          paint={paint}
        />
        {isCalibrated && (
          <Popup<AppleCountFeatureProperties>
            layer="apple-fill"
            component={CalibratedTreeCount}
          />
        )}
      </Source>

      <MapControl position="bottom-left" className="legend">
        <AppleCountLegend
          maxDensity={isCalibrated ? legendMax.toString() : t("label.High")}
          minDensity={isCalibrated ? legendMin.toString() : t("label.Low")}
          buckets={buckets}
          isCalibrated={isCalibrated}
          calibrationDate={calibrationDate}
        />
      </MapControl>
      {calibrationDate && (
        <YieldCard
          lastCalibratedDate={calibrationDate}
          yieldEstimate={yieldEstimate}
          manualCountsCount={manualCountsCount}
          averageAppleCountPerTree={averageAppleCountPerTree}
        />
      )}
      {error && <ErrorAlert onClose={() => setError("")} text={error} />}
    </>
  );
}
